import React, {useState, useEffect, useCallback, useRef} from 'react';
import classNames from 'classnames';
import {Dialog, Title, Scrollbar} from '@priority-software/priority-style-react';
import {SelectedList} from './SelectedList';
import {ListItem} from './ListItem';
import styles from './styles.module.scss';
import texts from '../../texts';
import {FormData} from '../DialogHeader';
import {settingsDialogWidth} from '../../constants';
import {getAvailableForms, getCollapsedSection, setCollapsedSection} from '../../utils/utils';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import {isRTL} from '../../../../Localization';
import {formsStore} from '../../../../_legacy/Form/stores/FormsStore';

type Props = {
    isOpen: boolean;
    closeSettings: () => void;
    currentShownForms: FormData[];
    updateInfoMap: (forms: FormData[]) => void;
    zoomFname: string;
    menuRef: React.RefObject<any>;
};

export const MAX_SHOWN_FORMS = 4;

const MenuTitle = ({text}) => {
    return (
        <Title textColor="primary-700" skin="h5" text={text} componentClasses={styles.menuTitle} />
    );
};

export const PanelSettings = ({
    isOpen,
    closeSettings,
    currentShownForms,
    updateInfoMap,
    zoomFname,
    menuRef
}: Props) => {
    const [shownForms, setShownForms] = useState<FormData[]>(currentShownForms);
    const hasUnsavedChanges = useRef(false);
    const [availableForms, setAvailableForms] = useState<any[]>([]);
    const position = menuRef.current && menuRef.current.getBoundingClientRect();

    useEffect(() => {
        if (isOpen && !hasUnsavedChanges.current) {
            const shownEntitysId = shownForms.map(form => form.entityId);
            getAvailableForms(shownEntitysId, zoomFname).then(res => {
                setAvailableForms(res);
            });
        }
    }, [isOpen, shownForms, zoomFname]);

    const handleChange = useCallback(() => {
        if (!hasUnsavedChanges.current) hasUnsavedChanges.current = true;
    }, []);

    const toggleChecked = (entityId: string, isChecked: boolean) => {
        const arrayOfExtraction = (isChecked ? shownForms : availableForms).slice();
        const arrayForInsertion = (isChecked ? availableForms : shownForms).slice();
        const index = arrayOfExtraction.findIndex(form => form.entityId === entityId);
        const item = arrayOfExtraction.splice(index, 1);
        if (isChecked) {
            setAvailableForms(
                [...arrayForInsertion, ...item].sort((a, b) => {
                    if (a.formTitle < b.formTitle) {
                        return -1;
                    } else if (a.formTitle > b.formTitle) {
                        return 1;
                    } else {
                        return 0;
                    }
                })
            );
            setShownForms(arrayOfExtraction);
        } else {
            setShownForms([...arrayForInsertion, ...item]);
            setAvailableForms(arrayOfExtraction);
        }

        handleChange();
    };

    const updateCollapsedSections = availableForms => {
        const data = getCollapsedSection();
        const currentCollapsed = data[zoomFname];
        if (!currentCollapsed) {
            return;
        }
        const availableFormsIds = availableForms.map(form => form.entityId);
        const updatedData = currentCollapsed.filter(entityId => {
            return !availableFormsIds.includes(entityId);
        });
        setCollapsedSection({...data, [zoomFname]: updatedData});
    };

    const handleClose = () => {
        if (hasUnsavedChanges.current) {
            updateCollapsedSections(availableForms);
            formsStore.currentForm.setCurrentFieldMoreInfo(shownForms);
            updateInfoMap(shownForms);
        }

        closeSettings();
    };

    const getMenuPosition = () => {
        const {top, left, right} = position;

        const menuPosition = {
            marginTop: top + 26 + 'px',
            marginLeft: isRTL() ? left + 'px' : 0,
            marginRight: isRTL() ? 0 : window.innerWidth - right + 'px'
        };
        return menuPosition;
    };

    const Content = (
        <div className={styles.contentContainer}>
            <div className={styles.selectedContainer}>
                <MenuTitle text={texts.selected_forms} />
                {shownForms.length === 1 && (
                    <ListItem
                        formTitle={shownForms[0].formTitle}
                        disabled={true}
                        checked={true}
                        tooltipText={texts.min_forms_tooltip}
                    />
                )}
                {shownForms.length > 1 && (
                    <SelectedList
                        data={shownForms}
                        toggleChecked={toggleChecked}
                        totalSelected={shownForms.length}
                        setShownForms={setShownForms}
                        handleChange={handleChange}
                    />
                )}
            </div>
            {availableForms.length > 0 && (
                <div className={styles.availableContainer}>
                    <div className={styles.availableItemsSection}>
                        <MenuTitle text={texts.available_forms} />
                        {availableForms.map((form, index) => {
                            const disabled = shownForms.length >= MAX_SHOWN_FORMS;
                            return (
                                <div
                                    key={form.entityId}
                                    onClick={() => !disabled && toggleChecked(form.entityId, false)}
                                    className={classNames(
                                        {
                                            [styles.borderTop]: index > 0
                                        },
                                        styles.margin
                                    )}
                                >
                                    <ListItem
                                        formTitle={form.formTitle}
                                        disabled={disabled}
                                        checked={false}
                                        tooltipText={texts.max_forms_tooltip}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );

    const listLength = shownForms.length + availableForms.length;

    return (
        position && (
            <Dialog
                isOpen={isOpen}
                closable={false}
                mask={false}
                maskClosable
                width={settingsDialogWidth}
                zIndex={getTopDialogZindex()}
                componentClasses={styles.settingsPanel}
                onClose={() => handleClose()}
                style={getMenuPosition()}
                content={
                    <div className={styles.settingsContainer}>
                        {listLength > 8 ? (
                            <Scrollbar
                                componentClasses={styles.scroll}
                                autoHeight
                                autoHeightMin={430}
                            >
                                {Content}
                            </Scrollbar>
                        ) : (
                            <>{Content}</>
                        )}
                    </div>
                }
            />
        )
    );
};
